import { DiamondLabels, DiamondShapeContent, colorName } from '../Utils/commonData';
import { source as diamondSourceLabels } from '../components/Diamond/useDiamond';

export const slugify = (value: string | undefined | null): string =>
  (value || '').toString().toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export const RB_PAGES = ['setting-plp', 'setting-config', 'diamond-plp', 'summary'] as const;

export const isValidPage = (value: string | undefined | null): boolean =>
  (RB_PAGES as readonly string[]).includes(value || '');

export const RB_CONFIG_OPTIONS: { [key: string]: string } = {
  Diamond_Shape: 'diamond-shape',
  Metal_Color: 'metal',
  Diamond_Type: 'diamond-type',
  Crown_Metal_Color: 'crown-metal',
  Center_Diamond_Size: 'center-diamond-size',
  Ring_Size: 'ring-size',
  Wedding_Band: 'matching-band',
  Side_Stones: 'side-stones',
  Earring_Back: 'earring-back',
};

export const toConfigOptionSlug = (key: string): string =>
  RB_CONFIG_OPTIONS[key] || slugify(key);

export const toConfigValueSlug = slugify;

const VULGAR_FRACTIONS: { [key: string]: number } = {
  '¼': 0.25, '½': 0.5, '¾': 0.75,
  '⅓': 1 / 3, '⅔': 2 / 3,
  '⅕': 0.2, '⅖': 0.4, '⅗': 0.6, '⅘': 0.8,
  '⅛': 0.125, '⅜': 0.375, '⅝': 0.625, '⅞': 0.875,
};

export const toCaratValue = (nameOrCode: string | number | undefined | null): string => {
  if (nameOrCode === undefined || nameOrCode === null || nameOrCode === '') return '';
  const raw = String(nameOrCode).trim();
  if (/^\d{3,}$/.test(raw)) return String(+raw / 100);

  const glyph = Object.keys(VULGAR_FRACTIONS).find((f) => raw.includes(f));
  const whole = raw.match(/\d+(?:\.\d+)?/);
  if (glyph) {
    return String(+((whole ? +whole[0] : 0) + VULGAR_FRACTIONS[glyph]).toFixed(2));
  }
  return whole ? String(+whole[0]) : '';
};

export const isValidConfigOption = (value: string | undefined | null): boolean =>
  Object.values(RB_CONFIG_OPTIONS).includes(value || '');

export const toMetalOptionValueSlug = (nameOrCode: string | undefined | null): string => {
  const slug = slugify(nameOrCode);
  if (!slug || slug.includes('platinum') || slug.endsWith('-gold')) return slug;
  return `${slug}-gold`;
};

export const toRingSizeValue = (nameOrCode: string | number | undefined | null): string => {
  if (nameOrCode === undefined || nameOrCode === null || nameOrCode === '') return '';
  const match = String(nameOrCode).trim().match(/\d+(?:\.\d+)?/);
  return match ? String(+match[0]) : '';
};

export const RB_MATCHING_BAND_COUNTS: { [key: string]: string } = {
  'no band': '0',
  'single':  '1',
  'double':  '2',
};

export const toMatchingBandValue = (
  label: string | undefined | null,
  styleId?: string | null
): string => {
  const count = RB_MATCHING_BAND_COUNTS[(label || '').toString().trim().toLowerCase()];
  if (count === undefined) return '';
  return styleId ? `${styleId}-${count}` : count;
};

export const RB_DIAMOND_SHAPE_SLUGS = DiamondShapeContent.map((shape) => slugify(shape.label));

export const toDiamondShapeSlug = (value: string | undefined | null): string => {
  if (!value) return '';
  const byCode = DiamondShapeContent.find((shape) => shape.code === value);
  return slugify(byCode ? byCode.label : value);
};

export const isValidDiamondShapeSlug = (value: string | undefined | null): boolean =>
  !value || RB_DIAMOND_SHAPE_SLUGS.includes(value);

export const RB_CENTER_DIAMOND_TYPE_SLUGS = diamondSourceLabels.map(slugify);

export const toCenterDiamondTypeSlug = (value: string | undefined | null): string => {
  if (!value) return '';
  if (value === 'GNU' || value === diamondSourceLabels[0]) return slugify(diamondSourceLabels[0]);
  if (value === diamondSourceLabels[1] || value.toUpperCase() === 'LGN') return slugify(diamondSourceLabels[1]);
  return slugify(value);
};

export const isValidCenterDiamondTypeSlug = (value: string | undefined | null): boolean =>
  !value || RB_CENTER_DIAMOND_TYPE_SLUGS.includes(value);

export const RB_METAL_COLOR_SLUGS = Object.keys(colorName).map((code) => slugify(`${colorName[code]} Gold`));

export const toMetalColorSlug = (code: string | undefined | null): string => {
  if (!code) return '';
  const label = colorName[code];
  return slugify(label ? `${label} Gold` : code);
};

export const isValidMetalColorSlug = (value: string | undefined | null): boolean =>
  !value || RB_METAL_COLOR_SLUGS.includes(value);

export const isValidRingStyleValue = (value: string | undefined | null): boolean =>
  value === undefined || value === null || value.trim().length > 0;

export const toRingStyleValue = (
  collections: unknown,
  ringStyleFallback?: string | null
): string => {
  const slugs = (Array.isArray(collections) ? collections : [])
    .map((entry) => slugify(entry === undefined || entry === null ? '' : String(entry)))
    .filter(Boolean);

  return slugs.length ? slugs.join(',') : slugify(ringStyleFallback);
};

export const toTrackingProductName = (
  settingName: string | undefined | null,
  diamond: { shape?: unknown; color?: unknown; clarity?: unknown } = {}
): string => {
  const shape = diamond.shape ? String(diamond.shape) : '';
  const diamondPart = [ DiamondLabels[shape] || shape, diamond.color, diamond.clarity ]
    .filter(part => part !== undefined && part !== null && `${part}`.trim().length > 0)
    .map(part => `${part}`.trim())
    .join(' ');

  return [ settingName, diamondPart ]
    .filter(part => part !== undefined && part !== null && `${part}`.trim().length > 0)
    .map(part => `${part}`.trim())
    .join(' + ');
};
